import React, {useEffect, useState} from "react";
import "./MaintenanceBanner.scss";

/**
 * Site-wide degraded-mode notice.
 *
 * Listens for the health events broadcast by services/api.js:
 *   "rd:api-degraded"  -> any API call hit a 5xx / network failure
 *   "rd:api-ok"        -> a later call succeeded, outage is over
 *
 * While degraded, the site keeps working from its static JSON fallbacks
 * (blogs-fallback.json / resumeData.json) — this banner just tells the
 * visitor why live data (comments, likes, latest posts) may be missing.
 */
export default function MaintenanceBanner() {
  const [degraded, setDegraded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onDegraded = () => setDegraded(true);
    const onOk = () => {
      setDegraded(false);
      setDismissed(false); // re-arm for the next outage
    };
    window.addEventListener("rd:api-degraded", onDegraded);
    window.addEventListener("rd:api-ok", onOk);
    return () => {
      window.removeEventListener("rd:api-degraded", onDegraded);
      window.removeEventListener("rd:api-ok", onOk);
    };
  }, []);

  if (!degraded || dismissed) return null;

  return (
    <div className="maintenance-banner" role="status" aria-live="polite">
      <span className="maintenance-banner__dot" aria-hidden="true" />
      <span className="maintenance-banner__text">
        Live content is temporarily unavailable — you're viewing a cached
        version of the site. Everything important still works.
      </span>
      <button
        type="button"
        className="maintenance-banner__close"
        aria-label="Dismiss notice"
        onClick={() => setDismissed(true)}
      >
        ×
      </button>
    </div>
  );
}
